import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";

import type { UserRecord } from "../../users/UserRecord";
import type { OrgUserRecord } from "../OrgService";

import { orgQueryKeys } from "./orgQueryKeys";

/**
 * Every member of `orgId`, with their roles.
 */
export function useOrgMembers(orgId: string | undefined) {
  const { orgService } = useDomainServices();

  return useQuery({
    queryKey: orgQueryKeys.members(orgId),
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error("Invalid orgId"));
      }
      return orgService.getOrgUsers(orgId, { abortSignal: signal });
    },
    enabled: orgId !== undefined,
  });
}

/** Index members by user id */
export function toMemberDirectory(
  members: OrgUserRecord[] | undefined,
): Map<string, UserRecord> {
  const directory = new Map<string, UserRecord>();
  members?.forEach((member) => directory.set(member.user.user_id, member.user));
  return directory;
}

/**
 * The org's members keyed by user id, for resolving an id to a name or avatar.
 */
export function useOrgMemberDirectory(
  orgId: string | undefined,
): Map<string, UserRecord> {
  const { data } = useOrgMembers(orgId);
  return React.useMemo(() => toMemberDirectory(data), [data]);
}
